import { useState, useMemo } from "react";
import { GitBranch, ChevronDown, ChevronRight } from "lucide-react";
import { DiffViewerWidget, type DiffFile } from "./DiffViewerWidget";

// Same gradient pool as AI chat bubbles (purple)
const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(160,105,255,0.14) 0%, rgba(125,75,235,0.09) 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

interface GitPushResultData {
  success?: boolean;
  nothing_to_push?: boolean;
  message?: string;
  branch?: string;
  commits?: number;
  error?: string;
  diff_files?: DiffFile[];
  diff_total_additions?: number;
  diff_total_deletions?: number;
}

interface GitPushResultWidgetProps {
  data: GitPushResultData;
}

export function GitPushResultWidget({ data }: GitPushResultWidgetProps) {
  if (!data) return null;
  const nothingToPush = data.nothing_to_push === true;
  const success = !nothingToPush && data.success !== false && !data.error;
  const hasDiff = data.diff_files && data.diff_files.length > 0;
  const [showDiff, setShowDiff] = useState(false);

  const gradient = useMemo(
    () => pickGradient(data.branch ?? data.message ?? "git-push"),
    [data.branch, data.message],
  );

  return (
    <div
      className={`animate-slide-up rounded-3xl rounded-bl-lg overflow-hidden ${
        success ? "max-w-2xl" : "max-w-lg"
      }`}
      style={{ background: gradient }}
    >
      {/* Result line */}
      <div className="px-4 py-3">
        <div className="flex items-baseline gap-2">
          <span
            className={`text-sm font-semibold ${
              nothingToPush
                ? "text-daydra-muted"
                : success
                  ? "text-daydra-success"
                  : "text-daydra-error"
            }`}
          >
            {nothingToPush ? "Nothing to push" : success ? "Pushed" : "Push failed"}
          </span>
          {data.branch && (
            <span className="text-2xs text-daydra-muted font-mono inline-flex items-center gap-1">
              <GitBranch size={10} className="shrink-0" />
              {data.branch}
            </span>
          )}
        </div>
        <p className="text-xs text-daydra-dim mt-0.5">
          {data.error ?? data.message ?? (success ? "Changes pushed to remote" : "Unknown error")}
        </p>
      </div>

      {/* Diff disclosure */}
      {hasDiff && success && (
        <>
          <button
            onClick={() => setShowDiff((v) => !v)}
            aria-expanded={showDiff}
            className="flex items-center gap-1.5 w-full px-4 py-1.5 text-2xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
          >
            {showDiff ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>{showDiff ? "Hide changes" : "Show changes"}</span>
            <span className="ml-auto font-mono text-daydra-success/70">
              +{data.diff_total_additions ?? 0}
            </span>
            <span className="font-mono text-daydra-error/70">
              -{data.diff_total_deletions ?? 0}
            </span>
          </button>
          {showDiff && (
            <DiffViewerWidget
              data={{
                files: data.diff_files!,
                total_files: data.diff_files!.length,
                total_additions: data.diff_total_additions ?? 0,
                total_deletions: data.diff_total_deletions ?? 0,
              }}
              embedded
            />
          )}
        </>
      )}
    </div>
  );
}
